import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ChevronDown } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_green-creations/artifacts/cf3sam7y_Untitled%20design.png";
const HERO_BG = "https://customer-assets.emergentagent.com/job_green-creations/artifacts/4o3qmxat_Small%20feet%20garden%20funriture.avif";
const BIRDPOT_VIDEO = "https://customer-assets.emergentagent.com/job_green-creations/artifacts/s5jkr4h1_Birdpot%20Animation.mp4";

const categories = [
  {
    title: "Birdpots",
    desc: "Where birds get better housing than most of us",
    to: "/birdpots",
    image: "https://customer-assets.emergentagent.com/job_green-creations/artifacts/ywuoviaj_IMG_6071.JPG",
    span: "md:col-span-7",
    height: "h-80 md:h-96",
    contain: true,
    containBg: "bg-[#1e3a1e]",
  },
  {
    title: "Furniture",
    desc: "Tables, benches & shelves with serious character",
    to: "/furniture",
    image: "https://customer-assets.emergentagent.com/job_green-creations/artifacts/kwc2gvb4_Small%20Feet%20chair%20and%20table%20set.avif",
    span: "md:col-span-5",
    height: "h-80 md:h-96",
  },
  {
    title: "Structures",
    desc: "Sheds, bandstands & things that make neighbours jealous",
    to: "/structures",
    image: "https://customer-assets.emergentagent.com/job_green-creations/artifacts/lz12ktgu_bandstand.png",
    span: "md:col-span-5",
    height: "h-80 md:h-96",
  },
  {
    title: "Conversions",
    desc: "Horseboxes, buses & builds from the chassis up",
    to: "/conversions",
    image: "https://customer-assets.emergentagent.com/job_green-creations/artifacts/5qfnbwi9_coffee%20hut%202.png",
    span: "md:col-span-7",
    height: "h-80 md:h-96",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.6, ease: "easeOut" },
  }),
};

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-[85vh] min-h-[600px] overflow-hidden" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 h-full flex flex-col justify-end pb-16 md:pb-24 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <img
              src={LOGO_URL}
              alt="Small Feet"
              className="h-24 md:h-32 w-auto object-contain mb-6 rounded"
              style={{ mixBlendMode: "multiply" }}
              data-testid="hero-logo"
            />
            <h1
              className="font-heading text-5xl sm:text-7xl tracking-tighter font-light text-[#222522] leading-[0.95]"
              data-testid="hero-title"
            >
              Old Wood.<br />
              New Tricks.
            </h1>
            <p className="font-body text-base md:text-lg text-[#5C635B] mt-6 max-w-lg leading-relaxed">
              We rescue forgotten materials and turn them into things you&apos;ll 
              actually want in your home. Wonky? Sometimes. Beautiful? Always.
            </p>
            <div className="flex gap-4 mt-8">
              <Link
                to="/furniture"
                className="btn-primary px-8 py-3 text-sm font-body tracking-wider uppercase inline-flex items-center gap-2"
                data-testid="hero-cta-explore"
              >
                See What We&apos;ve Made
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                className="px-8 py-3 text-sm font-body tracking-wider uppercase border border-[#3A5A3F] text-[#3A5A3F] hover:bg-[#3A5A3F] hover:text-[#F9F8F5] transition-all inline-flex items-center gap-2"
                data-testid="hero-cta-contact"
              >
                Say Hello
              </Link>
            </div>
          </motion.div>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
            <ChevronDown size={24} className="text-[#5C635B] scroll-bounce" />
          </div>
        </div>
      </section>

      {/* Marquee */}
      <section className="py-8 border-y border-[#D5CFBF]" data-testid="marquee-section">
        <Marquee speed={40} gradient={false}>
          <span className="marquee-text">One Person&apos;s Junk Is Our Jam</span>
          <span className="marquee-text">&bull;</span>
          <span className="marquee-text">Wonky Is Wonderful</span>
          <span className="marquee-text">&bull;</span>
          <span className="marquee-text">Made in Leicester</span>
          <span className="marquee-text">&bull;</span>
          <span className="marquee-text">No Two Pieces Alike</span>
          <span className="marquee-text">&bull;</span>
          <span className="marquee-text">Saving Wood Since Day One</span>
          <span className="marquee-text">&bull;</span>
          <span className="marquee-text">Built by Hand, Not by Machine</span>
          <span className="marquee-text">&bull;</span>
          <span className="marquee-text">Reduce, Reuse, Reclaim</span>
          <span className="marquee-text">&bull;</span>
        </Marquee>
      </section>

      {/* About Section */}
      <section className="py-24 md:py-32" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-5">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                custom={0}
                variants={fadeUp}
              >
                <p className="text-[#8B5E34] font-body text-sm tracking-widest uppercase mb-4">
                  Who Are We?
                </p>
                <h2 className="font-heading text-3xl sm:text-5xl tracking-tight font-light text-[#222522]">
                  We see treasure<br />where others see scrap
                </h2>
                <p className="font-body text-base text-[#5C635B] mt-6 leading-relaxed">
                  Small Feet started with a simple idea: stop perfectly good materials 
                  ending up in a skip. We rescue old timber, salvaged windows, forgotten 
                  doors — basically anything with a bit of character — and turn them 
                  into things people actually love.
                </p>
                <p className="font-body text-base text-[#5C635B] mt-4 leading-relaxed">
                  We&apos;re based in Leicester and we build everything by hand. Birdpots, 
                  furniture, mad garden structures, even full vehicle conversions from 
                  the chassis up. If it can be made from reclaimed materials, we&apos;ve 
                  probably had a go at it.
                </p>
              </motion.div>
            </div>
            <div className="md:col-span-7">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                custom={1}
                variants={fadeUp}
                className="relative rounded overflow-hidden bg-[#EAE5D9] flex items-center justify-center h-80 md:h-[450px]"
              >
                <img
                  src={LOGO_URL}
                  alt="Small Feet"
                  className="w-full h-full object-contain p-8 md:p-16"
                  data-testid="home-about-logo"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="py-24 md:py-32 bg-[#EAE5D9]" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            custom={0}
            variants={fadeUp}
            className="mb-12"
          >
            <p className="text-[#8B5E34] font-body text-sm tracking-widest uppercase mb-4">
              The Good Stuff
            </p>
            <h2 className="font-heading text-3xl sm:text-5xl tracking-tight font-light text-[#222522]">
              Have a Nosey
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            {categories.map((cat, i) => (
              <motion.div
                key={cat.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                custom={i}
                variants={fadeUp}
                className={`${cat.span}`}
              >
                <Link
                  to={cat.to}
                  className={`gallery-item block ${cat.height} relative rounded overflow-hidden ${cat.containBg || ""}`}
                  data-testid={`category-card-${cat.title.toLowerCase()}`}
                >
                  <img
                    src={cat.image}
                    alt={cat.title}
                    className={`w-full h-full ${cat.contain ? "object-contain p-2" : "object-cover"}`}
                  />
                  <div className="overlay">
                    <div>
                      <h3 className="font-heading text-2xl text-[#F9F8F5] mb-1">
                        {cat.title}
                      </h3>
                      <p className="font-body text-sm text-[#F9F8F5]/70">
                        {cat.desc}
                      </p>
                    </div>
                  </div>
                  {/* Always-visible label */}
                  <div className="absolute top-4 left-4 bg-[#F9F8F5]/90 px-4 py-2">
                    <span className="font-body text-xs tracking-widest uppercase text-[#222522]">
                      {cat.title}
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0}
            variants={fadeUp}
          >
            <img
              src={LOGO_URL}
              alt="Small Feet"
              className="h-20 w-auto object-contain mx-auto mb-8 rounded"
              style={{ mixBlendMode: "multiply" }}
              data-testid="cta-logo"
            />
            <h2 className="font-heading text-3xl sm:text-5xl tracking-tight font-light text-[#222522]">
              Got a bonkers idea?
            </h2>
            <p className="font-body text-base text-[#5C635B] mt-4 max-w-md mx-auto">
              Seriously, the weirder the better. We once turned a horse trailer into 
              a shower block. Try us.
            </p>
            <Link
              to="/contact"
              className="btn-primary mt-8 px-10 py-4 text-sm font-body tracking-wider uppercase inline-flex items-center gap-2"
              data-testid="cta-contact-link"
            >
              Challenge Us
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
